import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

/* 라오나AI 선금결제 — 라오나봇 이용료(계좌이체) 입금 확인 (백엔드 app/sstoa/api/laona_deposits.py)
   라오나봇은 무료로 시작하고, 사용량이 무료 한도를 넘어 회원이 결제를 고르면 입금 대기로 들어온다.
   통장 입금 내역의 입금자명 · 금액과 대조한 뒤 [입금 확인]을 누르면 회원이 이어서 진행하고 펌웨어 빌드까지 받는다. */

const FILTERS = [
  ["PENDING", "입금 대기"],
  ["PAID", "입금 확인"],
];
const STAGE = {
  DEPOSIT_PENDING: "입금 대기", ACTIVE: "대화 · 설계 중", DESIGNED: "설계 정리됨", APPROVED: "명세 승인",
  BUILT: "펌웨어 완성", ORDERED: "결산 입금 대기", PAID: "결산 입금 확인", SHIPPED: "발송됨", DELIVERED: "배송 완료",
  MANUAL: "직접 만들기로 넘김",
};
const STATE = { PENDING: "입금 대기", PAID: "입금 확인", FREE: "무료 사용 중", MANUAL: "직접 만들기" };

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(/Z|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "medium", timeStyle: "short" });
}
const won = (n) => `${(n || 0).toLocaleString("ko-KR")}원`;

function Row({ item, onDone, onError }) {
  const pending = item.status === "PENDING";
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function confirm() {
    if (!window.confirm(`${item.depositor} 님의 이용료 ${won(item.deposit)} 입금을 확인할까요?\n확인하면 회원이 이어서 진행하고 펌웨어 빌드까지 받을 수 있으며, 메일이 갑니다.`)) return;
    setBusy(true);
    try {
      await api.post(`/laona-deposits/${item.id}/confirm`, { note });
      await onDone();
    } catch (err) { onError(err.message); } finally { setBusy(false); }
  }
  async function revert() {
    if (!window.confirm("입금 확인을 되돌릴까요? 무료 한도를 넘긴 회원은 라오나봇 대화가 다시 닫힙니다.")) return;
    setBusy(true);
    try {
      await api.post(`/laona-deposits/${item.id}/revert`, {});
      await onDone();
    } catch (err) { onError(err.message); } finally { setBusy(false); }
  }

  return (
    <tr>
      <td>
        <span className={`sstoa-tag ${pending ? "" : "sstoa-tag--gray"}`} style={{ whiteSpace: "nowrap" }}>{STATE[item.status] || item.status}</span>
      </td>
      <td style={{ fontFamily: "ui-monospace, monospace" }}>#{item.id}</td>
      <td>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{item.depositor || "—"}</div>
        <div style={{ fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>입금자명</div>
      </td>
      <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{won(item.deposit)}</td>
      <td>
        {item.member ? item.member.name : "—"}
        <div style={{ fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>{item.member?.email}</div>
      </td>
      <td>
        <div style={{ fontWeight: 600 }}>
          {item.title}
          {item.deleted_at && <span className="sstoa-tag sstoa-tag--gray" style={{ marginLeft: 6 }} title={`회원이 ${formatWhen(item.deleted_at)}에 지움`}>회원이 삭제함</span>}
        </div>
        <div style={{ fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>
          {STAGE[item.project_status] || item.project_status}
          {!pending && ` · 대화 ${item.messages}건 · 사용 ${won(item.usage_krw)}`}
        </div>
      </td>
      <td style={{ whiteSpace: "nowrap" }}>
        {formatWhen(item.created_at)}
        {item.paid_at && <div style={{ fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>확인 {formatWhen(item.paid_at)}</div>}
      </td>
      <td style={{ minWidth: 220 }}>
        {pending ? (
          <div style={{ display: "flex", gap: 6 }}>
            <input className="sstoa-input" style={{ flex: 1, minWidth: 0 }} value={note} placeholder="메모 (선택)"
                   onChange={(e) => setNote(e.target.value)} disabled={busy} />
            <button className="sstoa-btn sstoa-btn--sm sstoa-btn--primary" onClick={confirm} disabled={busy}>
              입금 확인
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {item.note && <span className="sstoa-hint">{item.note}</span>}
            {item.status === "PAID" && ["ACTIVE", "DESIGNED"].includes(item.project_status) && !item.has_build && (
              <button className="sstoa-btn sstoa-btn--sm" onClick={revert} disabled={busy}>확인 되돌리기</button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

export default function LaonaDepositsAdmin() {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState(null);
  const [filter, setFilter] = useState("PENDING");
  const [error, setError] = useState("");

  async function load(status = filter) {
    setError("");
    window.dispatchEvent(new Event("sstoa:laona-deposits")); // 왼쪽 메뉴 배지도 다시 센다
    try {
      const [list, c] = await Promise.all([
        api.get(`/laona-deposits${status ? `?status=${status}` : ""}`),
        api.get("/laona-deposits/counts"),
      ]);
      setRows(list);
      setCounts(c);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    load("PENDING");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onFilter(s) { setFilter(s); load(s); }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/sstoa" className="sstoa-back">← 홈</Link>
      </div>

      <div className="sstoa-topbar">
        <div>
          <h1 className="sstoa-title">라오나AI 선금결제</h1>
          <p className="sstoa-sub">
            라오나봇 이용료(계좌이체)입니다. 라오나봇은 무료로 시작하고, 사용량이 무료 한도를 넘어 회원이 결제를 고르면
            입금 대기로 들어옵니다. 통장 입금 내역의 입금자명 · 금액과 대조한 뒤 [입금 확인]을 누르면 회원이 이어서 진행하고
            펌웨어 빌드까지 받을 수 있으며, 회원에게 확인 메일이 갑니다.
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {FILTERS.map(([v, l]) => (
          <button key={v} className={`sstoa-btn sstoa-btn--sm${filter === v ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter(v)}>
            {l}{counts ? ` ${counts[v] || 0}` : ""}
          </button>
        ))}
        <button className={`sstoa-btn sstoa-btn--sm${filter === "" ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter("")}>
          전체{counts ? ` ${counts.total}` : ""}
        </button>
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="sstoa-empty">{filter === "PENDING" ? "입금 대기 중인 이용료가 없습니다." : "내역이 없습니다."}</div>
        ) : (
          <table className="sstoa-table">
            <thead>
              <tr><th>상태</th><th>번호</th><th>입금자</th><th>금액</th><th>회원</th><th>프로젝트</th><th>신청일</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((it) => <Row key={it.id} item={it} onDone={() => load()} onError={setError} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
